// "Viewing as [Persona]" selector — the view-lens picker. A compact chip in
// the top-right TopBar (see App.tsx / TopBar.tsx); clicking opens a menu of
// all personas with descriptions so the swap feels like choosing a mode.
// The choice persists via PersonaProvider (localStorage).
import { useEffect, useRef, useState } from "react";
import type { Persona } from "@release-hub/shared";
import {
  PERSONA_DESCRIPTION,
  PERSONA_LABEL,
  usePersona,
} from "@/auth/Persona";
import { cn } from "@/lib/cn";

const ORDER: Persona[] = ["epd", "enablement", "sales"];

/** Plain-English subtitle under the persona label — makes "EPD" legible
 *  and shows the sub-roles for the Enablement lens. */
const PERSONA_TITLE: Record<Persona, string> = {
  epd: "Engineering / Product / Design",
  enablement: "PMM · CS · Sales Engineering",
  sales: "Go-to-market",
};

export function PersonaSelector() {
  const { persona, setPersona } = usePersona();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Click-away + Escape to close. Attached only while open to avoid
  // unnecessary listener churn on every render.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (p: Persona) => {
    setPersona(p);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          // w-full so the button shrinks WITH its min-w-0 wrapper instead of
          // sizing to its content and spilling over the neighbouring search
          // control; the label then truncates in-bounds.
          "flex w-full min-w-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 text-[12.5px] transition-colors",
          open
            ? "border-accent bg-card text-text shadow-sm"
            : "border-border-hover bg-card/60 text-text-secondary hover:border-accent/60 hover:text-text",
        )}
        title="Change your view lens"
      >
        <span className="hidden text-text-tertiary sm:inline">Viewing as</span>
        {/* Truncate rather than wrap: on a phone the row is at its limit, and a
            two-line label would inflate the whole fixed bar. */}
        <span className="min-w-0 truncate font-medium text-text">
          {PERSONA_LABEL[persona]}
        </span>
        <Chevron open={open} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-40 mt-1 w-72 overflow-hidden rounded-lg border border-border bg-card shadow-lg"
        >
          {ORDER.map((p) => {
            const active = p === persona;
            return (
              <button
                key={p}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => select(p)}
                className={cn(
                  "flex w-full items-start gap-2.5 border-b border-border/60 px-3 py-2.5 text-left last:border-b-0 transition-colors",
                  active ? "bg-border/40" : "hover:bg-border/25",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "mt-1 h-2 w-2 shrink-0 rounded-full",
                    active ? "bg-accent" : "bg-border-hover",
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="font-serif text-sm text-text">
                      {PERSONA_LABEL[p]}
                    </span>
                    <span className="truncate text-[11px] text-text-tertiary">
                      {PERSONA_TITLE[p]}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-text-secondary">
                    {PERSONA_DESCRIPTION[p]}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      className={cn(
        "h-3 w-3 shrink-0 text-text-tertiary transition-transform",
        open && "rotate-180",
      )}
      aria-hidden
    >
      <path
        d="M2.5 4.5l3.5 3.5 3.5-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
